import { Button, ProgressCircle, TextArea } from '@react-spectrum/s2';
import AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import Publish from '@react-spectrum/s2/icons/Publish';
import PublishNo from '@react-spectrum/s2/icons/PublishNo';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { AvatarCircle } from '@/components/artifact/AvatarCircle';
import { GenericReview } from '@/components/artifact/GenericReview';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { useJobActivation } from '@/components/artifact/useJobActivation';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { UserSettingsContext } from '@/spaces/settings/UserSettingsContext';
import { cn } from '@/utils/cn';
import { trackEvent } from '@/utils/fullstory';

import {
	isPartialCommentReplySend,
	type Artifact,
	type IGCommentReplyContent,
} from '../../../../../bridge/types/artifact.ts';

interface IGCommentReplyArtifactPanelProps {
	artifact: Artifact;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

function isIGCommentReplyContent(
	content: Record<string, unknown>,
): content is IGCommentReplyContent {
	return (
		typeof content.comment_id === 'string' &&
		typeof content.comment_text === 'string' &&
		typeof content.commenter_username === 'string' &&
		typeof content.commenter_ig_scoped_id === 'string' &&
		typeof content.response_type === 'string'
	);
}

function formatStatusTimestamp(timestamp: string): string {
	return new Date(timestamp).toLocaleString([], {
		dateStyle: 'medium',
		timeStyle: 'short',
	});
}

function StatusBanner({
	label,
	timestamp,
	action,
	detail,
}: {
	label: 'Sent' | 'Skipped' | 'Send failed' | 'Reply sent, DM failed';
	timestamp: string;
	action?: React.ReactNode;
	detail?: string;
}) {
	const icon =
		label === 'Sent' ? (
			<Publish styles={iconStyle({ size: 'XS' })} />
		) : label === 'Skipped' ? (
			<PublishNo styles={iconStyle({ size: 'XS' })} />
		) : (
			<AlertTriangle styles={iconStyle({ size: 'XS' })} />
		);

	return (
		<div className="glass-card-sm flex flex-col gap-2 px-4 py-3">
			<div className="flex flex-wrap items-center gap-3">
				<span className="inline-flex min-w-0 flex-1 items-center gap-2">
					<span
						className="inline-flex h-[18px] w-[18px] items-center
							justify-center"
					>
						{icon}
					</span>
					<span className="text-sm font-medium text-(--text-primary)">
						{label}
					</span>
					<span className="text-sm text-(--text-secondary)">
						{formatStatusTimestamp(timestamp)}
					</span>
				</span>
				{action}
			</div>
			{detail && (
				<div className="flex flex-col gap-2">
					<hr className="border-(--glass-border)" />
					<span className="text-sm text-(--text-secondary)">{detail}</span>
				</div>
			)}
		</div>
	);
}

export const IGCommentReplyArtifactPanel = ({
	artifact,
	onBack,
	onClose,
	style,
}: IGCommentReplyArtifactPanelProps) => {
	const bridgeApi = useBridgeApi();
	const settings = useContext(UserSettingsContext)?.settings;
	const { handleActivate: handleApprove, activatedView } = useJobActivation({
		artifact,
		title: 'Reply approved',
		summary: "I'll post this reply to the comment on Instagram.",
		onClose,
		target: 'artifact',
	});
	const [actionState, setActionState] = useState<
		'idle' | 'sending' | 'retrying' | 'skipping' | 'failed'
	>('idle');

	const typedContent = useMemo(() => {
		return isIGCommentReplyContent(artifact.content) ? artifact.content : null;
	}, [artifact.content]);

	const [replyText, setReplyText] = useState(typedContent?.reply_text ?? '');
	const [dmText, setDmText] = useState(typedContent?.dm_text ?? '');

	useEffect(() => {
		setReplyText(typedContent?.reply_text ?? '');
		setDmText(typedContent?.dm_text ?? '');
	}, [typedContent?.reply_text, typedContent?.dm_text]);

	useEffect(() => {
		if (artifact.status === 'failed') {
			setActionState('failed');
		} else if (artifact.status === 'processing') {
			setActionState((current) =>
				current === 'retrying' ? 'retrying' : 'sending',
			);
		} else {
			setActionState('idle');
		}
	}, [artifact.status]);

	const commitContent = useCallback(
		(updates: Record<string, unknown>) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: updates,
			});
		},
		[bridgeApi, artifact.id],
	);

	const handleSkip = useCallback(() => {
		trackEvent('IG Comment Reply Skipped', { artifact_id: artifact.id });
		setActionState('skipping');
		bridgeApi.sendAction({
			type: 'do_reject_artifact',
			artifact_id: artifact.id,
		});
	}, [bridgeApi, artifact.id]);

	const handleRetry = useCallback(() => {
		trackEvent('IG Comment Reply Retry Clicked', {
			artifact_id: artifact.id,
			process_id: artifact.process_id,
		});
		setActionState('retrying');
		bridgeApi.sendAction({
			type: 'do_activate_artifact',
			artifact_id: artifact.id,
		});
	}, [bridgeApi, artifact.id, artifact.process_id]);

	const canReview = isArtifactActionable(artifact);
	const isReadOnly =
		!canReview ||
		actionState === 'sending' ||
		actionState === 'retrying' ||
		actionState === 'skipping';
	const showReply =
		typedContent?.response_type === 'reply-only' ||
		typedContent?.response_type === 'reply-and-dm';
	const showDm =
		typedContent?.response_type === 'dm-only' ||
		typedContent?.response_type === 'reply-and-dm';
	const businessUsername =
		settings?.instagram_connection?.ig_username ||
		settings?.instagram_handle ||
		'yourbusiness';
	const hasPostContext =
		!!typedContent?.post_image_url || !!typedContent?.post_caption;
	const activationError = typedContent?.activation_error || null;
	// The reply reached Instagram but the DM did not. The watcher hands a failed
	// auto-publish back as a draft, so this can hold for any status, and it has
	// to be tested before every other one. Otherwise the panel calls a posted
	// reply unsent, or claims the whole send worked.
	// The rule itself is shared with the bridge, which writes the review
	// thread's message from the same call. Two local copies disagreed, so one
	// send could get two opposite descriptions on two screens.
	// A skipped reply is skipped, whatever it managed to send first. A part-send
	// keeps its activation_error and reply_posted_at when the customer skips it,
	// and only `status` moves to cancelled. Without this the partial banner and
	// its Retry button stayed on screen, and one press re-activated the
	// cancelled artifact and sent the direct message the customer chose not to
	// send. That send cannot be undone.
	const isCancelled = artifact.status === 'cancelled';
	const isPartialSend = !isCancelled && isPartialCommentReplySend(typedContent);
	// The watcher hands a failed auto-publish back as a draft, so a part-send can
	// be actionable. Approve then dispatches the same do_activate_artifact the
	// banner's Retry does, under copy that says the public reply is going out
	// now. It went out earlier, so that copy is false. Skip stays: it is the only
	// way to decline the channel that never went out.
	// This is deliberately not folded into canReview. isReadOnly reads canReview,
	// and the DM never reached Instagram, so its text must stay editable.
	const canApprove = canReview && !isPartialSend;
	const isSent =
		!isPartialSend &&
		(artifact.status === 'scheduled' || artifact.status === 'published');
	const isSkipped = isCancelled;
	const isRetrying = actionState === 'retrying';
	const isSendFailed =
		!isPartialSend && (artifact.status === 'failed' || isRetrying);
	const retryAction = (
		<Button
			type="button"
			variant="primary"
			onPress={handleRetry}
			isPending={isRetrying}
		>
			Retry
		</Button>
	);
	const statusBanner = isPartialSend ? (
		<StatusBanner
			label="Reply sent, DM failed"
			timestamp={artifact.updated_at}
			detail={activationError ?? undefined}
			action={retryAction}
		/>
	) : isSent ? (
		<StatusBanner label="Sent" timestamp={artifact.updated_at} />
	) : isSkipped ? (
		<StatusBanner label="Skipped" timestamp={artifact.updated_at} />
	) : isSendFailed ? (
		<StatusBanner
			label="Send failed"
			timestamp={artifact.updated_at}
			detail={activationError ?? undefined}
			action={retryAction}
		/>
	) : null;

	const actionRenderer =
		canReview || actionState === 'sending' || actionState === 'skipping'
			? () => (
					<>
						{actionState === 'sending' && (
							<div
								className="mr-auto flex items-center gap-2 text-sm font-medium
									text-gray-700"
							>
								<ProgressCircle
									isIndeterminate
									size="S"
									aria-label="Sending reply"
								/>
								<span>Sending...</span>
							</div>
						)}
						{actionState === 'skipping' && (
							<div
								className="mr-auto flex items-center gap-2 text-sm font-medium
									text-gray-700"
							>
								<ProgressCircle
									isIndeterminate
									size="S"
									aria-label="Skipping reply"
								/>
								<span>Skipping...</span>
							</div>
						)}
						{canReview &&
							actionState !== 'sending' &&
							actionState !== 'skipping' && (
								<Button type="button" variant="secondary" onPress={handleSkip}>
									Skip
								</Button>
							)}
						{canApprove &&
							actionState !== 'sending' &&
							actionState !== 'skipping' && (
								<Button type="button" variant="accent" onPress={handleApprove}>
									Approve
								</Button>
							)}
					</>
				)
			: undefined;

	const title = TYPE_TITLES[artifact.type] ?? 'Review Reply';

	return (
		<ChatPanelLayout
			title={title}
			description={
				activatedView
					? undefined
					: artifact.user_summary || artifact.description || undefined
			}
			actionRenderer={activatedView ? undefined : actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{activatedView ??
				(typedContent ? (
					<div className="flex flex-col gap-6">
						{statusBanner}

						{hasPostContext && (
							<div
								className={cn(
									'glass-card-sm flex-col overflow-hidden',
									typedContent.post_image_url
										? 'inline-flex self-center'
										: 'flex',
								)}
							>
								{typedContent.post_image_url && (
									<img
										src={typedContent.post_image_url}
										alt="Post"
										className="max-h-[40vh] max-w-full object-contain"
									/>
								)}
								<div className="w-0 min-w-full px-4 py-3">
									<p
										className="line-clamp-3 text-sm leading-snug wrap-break-word
											text-(--text-secondary)"
									>
										<span className="font-semibold text-(--text-primary)">
											{businessUsername}
										</span>{' '}
										{typedContent.post_caption || '(No caption)'}
									</p>
								</div>
							</div>
						)}

						<div className="flex items-start gap-3">
							<AvatarCircle username={typedContent.commenter_username} />
							<div className="flex flex-col gap-1 pt-1">
								<span className="text-sm font-bold text-(--text-primary)">
									{typedContent.commenter_username}
								</span>
								<p
									className="line-clamp-5 text-base leading-relaxed
										text-(--text-secondary)"
								>
									{typedContent.comment_text}
								</p>
							</div>
						</div>

						<hr className="border-(--glass-border)" />

						<div className="flex flex-col gap-4">
							{showReply && (
								<div className="flex flex-col gap-2">
									<span
										className="text-xs font-bold tracking-wide
											text-(--text-muted)"
									>
										Reply to {typedContent.commenter_username}
									</span>
									<TextArea
										aria-label="Your reply"
										value={replyText}
										onChange={setReplyText}
										onBlur={() => {
											if (replyText !== (typedContent.reply_text ?? '')) {
												commitContent({ reply_text: replyText });
											}
										}}
										isReadOnly={isReadOnly}
									/>
								</div>
							)}

							{showDm && (
								<div className="flex flex-col gap-2">
									<span
										className="text-xs font-bold tracking-wide
											text-(--text-muted)"
									>
										Direct message to {typedContent.commenter_username}
									</span>
									<TextArea
										aria-label="Your DM"
										value={dmText}
										onChange={setDmText}
										onBlur={() => {
											if (dmText !== (typedContent.dm_text ?? '')) {
												commitContent({ dm_text: dmText });
											}
										}}
										isReadOnly={isReadOnly}
									/>
								</div>
							)}
						</div>
					</div>
				) : (
					<GenericReview
						content={artifact.content}
						artifactType={artifact.type}
					/>
				))}
		</ChatPanelLayout>
	);
};
